// Smart Daara - Parent Management Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { 
  Users, Plus, Search, MoreVertical, Trash2, Pencil,
  Loader2, Mail, Phone, Link2, UserCheck, UserX, GraduationCap, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import PasswordResetDialog from '../../components/shared/PasswordResetDialog';

export default function ParentManagement() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [linkChildrenModalOpen, setLinkChildrenModalOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    child_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [parentsRes, studentsRes] = await Promise.all([
        userAPI.listParents({ tenant_id: user?.tenant_id }),
        userAPI.listStudents({ tenant_id: user?.tenant_id })
      ]);
      setParents(parentsRes.data?.items || parentsRes.data || []);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      password: '',
      child_ids: []
    });
  };

  const handleCreateParent = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.password) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await userAPI.createParent({
        ...formData,
        tenant_id: user?.tenant_id,
        role: 'parent'
      });
      toast.success(language === 'fr' ? 'Parent créé avec succès!' : 'Parent created successfully!');
      setCreateModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating parent:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditParent = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await userAPI.updateParent(selectedParent.id, {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone
      });
      toast.success(language === 'fr' ? 'Parent mis à jour!' : 'Parent updated!');
      setEditModalOpen(false);
      setSelectedParent(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error updating parent:', error);
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteParent = async () => {
    if (!selectedParent) return;

    setSaving(true);
    try {
      await userAPI.deleteParent(selectedParent.id);
      toast.success(language === 'fr' ? 'Parent supprimé!' : 'Parent deleted!');
      setDeleteModalOpen(false);
      setSelectedParent(null);
      loadData();
    } catch (error) {
      console.error('Error deleting parent:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (parent) => {
    try {
      await userAPI.updateParent(parent.id, { is_active: !parent.is_active });
      toast.success(language === 'fr' 
        ? (parent.is_active ? 'Parent désactivé' : 'Parent activé')
        : (parent.is_active ? 'Parent deactivated' : 'Parent activated')
      );
      loadData();
    } catch (error) {
      console.error('Error toggling parent:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const openEditModal = (parent) => {
    setSelectedParent(parent);
    setFormData({
      email: parent.email || '',
      first_name: parent.first_name || '',
      last_name: parent.last_name || '',
      phone: parent.phone || '',
      password: '',
      child_ids: parent.child_ids || []
    });
    setEditModalOpen(true);
  };

  const openLinkChildrenModal = (parent) => {
    setSelectedParent(parent);
    setFormData(prev => ({ ...prev, child_ids: parent.child_ids || [] }));
    setLinkChildrenModalOpen(true);
  };

  const handleLinkChildren = async () => {
    setSaving(true);
    try {
      await userAPI.linkParentChildren(selectedParent.id, formData.child_ids);
      toast.success(language === 'fr' ? 'Enfants associés avec succès!' : 'Children linked successfully!');
      setLinkChildrenModalOpen(false);
      setSelectedParent(null);
      loadData();
    } catch (error) {
      console.error('Error linking children:', error);
      toast.error(language === 'fr' ? 'Erreur d\'association' : 'Linking error');
    } finally {
      setSaving(false);
    }
  };

  const toggleChildSelection = (studentId) => {
    setFormData(prev => ({
      ...prev,
      child_ids: prev.child_ids.includes(studentId)
        ? prev.child_ids.filter(id => id !== studentId)
        : [...prev.child_ids, studentId]
    }));
  };

  const filteredParents = parents.filter(p => 
    p.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getChildrenNames = (parent) => {
    if (!parent.child_ids || parent.child_ids.length === 0) return '-';
    return parent.child_ids.map(childId => {
      const child = students.find(s => s.id === childId);
      return child ? `${child.first_name} ${child.last_name}` : '';
    }).filter(Boolean).join(', ') || '-';
  };

  return (
    <div className="space-y-6" data-testid="parent-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Parents' : 'Parent Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? `${filteredParents.length} parent(s) enregistré(s)`
              : `${filteredParents.length} parent(s) registered`
            }
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800" 
          onClick={() => setCreateModalOpen(true)}
          data-testid="add-parent-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Ajouter un Parent' : 'Add Parent'}
        </Button>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder={language === 'fr' ? 'Rechercher par nom ou email...' : 'Search by name or email...'}
              className="pl-9" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Parents List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            {language === 'fr' ? 'Liste des Parents' : 'Parent List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredParents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{language === 'fr' ? 'Aucun parent trouvé' : 'No parents found'}</p>
              <Button 
                variant="outline" 
                className="mt-3"
                onClick={() => setCreateModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Ajouter un parent' : 'Add a parent'}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Parent' : 'Parent'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Enfants' : 'Children'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParents.map((parent) => (
                    <tr key={parent.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {parent.first_name} {parent.last_name}
                            </p>
                            {parent.phone && (
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {parent.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {parent.email}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => openLinkChildrenModal(parent)}
                        >
                          <GraduationCap className="w-3 h-3" />
                          {parent.child_ids?.length || 0} {language === 'fr' ? 'enfant(s)' : 'child(ren)'}
                          <Link2 className="w-3 h-3" />
                        </Button>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={parent.is_active !== false
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                        }>
                          {parent.is_active !== false 
                            ? (language === 'fr' ? 'Actif' : 'Active')
                            : (language === 'fr' ? 'Inactif' : 'Inactive')
                          }
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(parent)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Modifier' : 'Edit'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openLinkChildrenModal(parent)}>
                              <Link2 className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Associer des enfants' : 'Link Children'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(parent)}>
                              {parent.is_active !== false ? (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Désactiver' : 'Deactivate'}
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Activer' : 'Activate'}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedParent(parent);
                                setResetPasswordOpen(true);
                              }}
                              data-testid={`reset-password-parent-${parent.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Réinitialiser mot de passe' : 'Reset password'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedParent(parent);
                                setDeleteModalOpen(true);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Supprimer' : 'Delete'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Ajouter un Parent' : 'Add Parent'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Créez un compte parent pour votre école'
                : 'Create a parent account for your school'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                  placeholder="Fatou"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                  placeholder="Ndiaye"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder="parent@email.sn"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                placeholder="+221 77 000 0000"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Mot de passe' : 'Password'} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateModalOpen(false); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateParent}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Ajouter' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Modifier le Parent' : 'Edit Parent'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditModalOpen(false); setSelectedParent(null); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleEditParent}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Children Modal */}
      <Dialog open={linkChildrenModalOpen} onOpenChange={setLinkChildrenModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' 
                ? `Associer des enfants à ${selectedParent?.first_name} ${selectedParent?.last_name}`
                : `Link children to ${selectedParent?.first_name} ${selectedParent?.last_name}`
              }
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Sélectionnez les étudiants qui sont les enfants de ce parent'
                : 'Select the students who are this parent\'s children'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 max-h-[400px] overflow-y-auto">
            {students.length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                {language === 'fr' ? 'Aucun étudiant disponible' : 'No students available'}
              </p>
            ) : (
              <div className="space-y-2">
                {students.map((student) => (
                  <div 
                    key={student.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer ${
                      formData.child_ids.includes(student.id) ? 'border-emerald-300 bg-emerald-50' : ''
                    }`}
                    onClick={() => toggleChildSelection(student.id)}
                  >
                    <input 
                      type="checkbox"
                      checked={formData.child_ids.includes(student.id)}
                      onChange={() => toggleChildSelection(student.id)}
                      className="w-4 h-4 text-emerald-600"
                    />
                    <GraduationCap className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{student.first_name} {student.last_name}</p>
                      <p className="text-xs text-slate-500">{student.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setLinkChildrenModalOpen(false); setSelectedParent(null); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleLinkChildren}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Associer' : 'Link'} ({formData.child_ids.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {language === 'fr' ? 'Supprimer le Parent' : 'Delete Parent'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer ${selectedParent?.first_name} ${selectedParent?.last_name}?`
                : `Are you sure you want to delete ${selectedParent?.first_name} ${selectedParent?.last_name}?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleDeleteParent}
              disabled={saving}
              variant="destructive"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <PasswordResetDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
        user={selectedParent}
        userId={selectedParent?.user_id}
      />
    </div>
  );
}
